import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Lock, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { SEO } from '../components/SEO';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="privacy-page">
      <SEO title="Privacy Policy" description="Learn how Supremics protects your privacy and handles your data." />
      
      {/* Hero */}
      <div className="relative bg-gradient-to-b from-[var(--surface)] to-[var(--background)] border-b border-[var(--border)]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--primary)]/5 rounded-full blur-3xl" />
        </div>
        <div className="relative page-padding py-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="text-[var(--primary)]" size={40} />
          </div>
          <h1 className="text-4xl md:text-5xl font-teko font-semibold mb-4">Privacy Policy</h1>
          <p className="text-[var(--text-secondary)] max-w-xl mx-auto">
            Last updated: February 2, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="page-padding py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-8 space-y-8">
            
            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                Introduction
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                At Supremics, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
                disclose, and safeguard your information when you visit our website and use our services. 
                Please read this privacy policy carefully. By using Supremics, you consent to the data practices 
                described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                Information We Collect
              </h2>
              <div className="space-y-4 text-[var(--text-secondary)]">
                <div className="flex gap-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-[var(--foreground)]">Personal Information</p>
                    <p>Name, email address, username, and profile picture when you create an account.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-[var(--foreground)]">Payment Information</p>
                    <p>Billing details processed securely through Stripe. We do not store credit card numbers.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-[var(--foreground)]">Usage Data</p>
                    <p>Reading history, likes, comments, and interaction patterns to improve your experience.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-[var(--foreground)]">Device Information</p>
                    <p>Browser type, IP address, and device identifiers for security and analytics.</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                How We Use Your Information
              </h2>
              <ul className="space-y-2 text-[var(--text-secondary)]">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  To provide and maintain our services
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  To process payments and subscriptions
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  To personalize your reading experience
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  To communicate with you about updates and promotions
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  To detect and prevent fraud or abuse
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  To comply with legal obligations
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                Data Sharing
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed mb-4">
                We do not sell your personal information. We may share data with:
              </p>
              <ul className="space-y-2 text-[var(--text-secondary)]">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--secondary)] rounded-full mt-2" />
                  <span><strong>Service providers:</strong> Payment processors (Stripe), analytics (Google Analytics), email services</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--secondary)] rounded-full mt-2" />
                  <span><strong>Creators:</strong> Aggregated, anonymized analytics about their content performance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--secondary)] rounded-full mt-2" />
                  <span><strong>Legal requirements:</strong> When required by law or to protect our rights</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                Your Rights
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-[var(--background)] rounded-xl border border-[var(--border)]">
                  <Lock size={20} className="text-[var(--primary)] mb-2" />
                  <p className="font-medium">Access & Portability</p>
                  <p className="text-sm text-[var(--text-secondary)]">Request a copy of your data at any time</p>
                </div>
                <div className="p-4 bg-[var(--background)] rounded-xl border border-[var(--border)]">
                  <FileText size={20} className="text-[var(--primary)] mb-2" />
                  <p className="font-medium">Correction</p>
                  <p className="text-sm text-[var(--text-secondary)]">Update or correct inaccurate information</p>
                </div>
                <div className="p-4 bg-[var(--background)] rounded-xl border border-[var(--border)]">
                  <AlertCircle size={20} className="text-[var(--primary)] mb-2" />
                  <p className="font-medium">Deletion</p>
                  <p className="text-sm text-[var(--text-secondary)]">Request deletion of your account and data</p>
                </div>
                <div className="p-4 bg-[var(--background)] rounded-xl border border-[var(--border)]">
                  <Users size={20} className="text-[var(--primary)] mb-2" />
                  <p className="font-medium">Opt-Out</p>
                  <p className="text-sm text-[var(--text-secondary)]">Unsubscribe from marketing communications</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                Contact Us
              </h2>
              <p className="text-[var(--text-secondary)] mb-4">
                If you have questions about this Privacy Policy, please contact us:
              </p>
              <div className="p-4 bg-[var(--background)] rounded-xl border border-[var(--border)]">
                <p className="text-[var(--foreground)]">Email: privacy@supremics.com</p>
                <p className="text-[var(--text-secondary)]">Response time: Within 48 hours</p>
              </div>
            </section>
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/" className="text-[var(--primary)] hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
